'use client'

import { useState } from 'react'
import { Pencil, Trash2, ChevronDown, X } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { toast } from 'sonner'

import InstagramPostCard from '@/components/InstagramPostCard'
import LinkedInPostCard from '@/components/LinkedInPostCard'
import TikTokPostCard from '@/components/TikTokPostCard'
import FacebookPostCard from '@/components/FacebookPostCard'
import PostSchedulePopover from '@/components/PostSchedulePopover'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { api } from '@/utils/trpc'

type SocialPostDialogProps = {
  postId: string | null
  onOpenChangeAction: (open: boolean) => void
}

function formatPublishAt(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale === 'de' ? 'de-DE' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
    hour12: locale !== 'de',
  }).format(date)
}

export default function SocialPostDialog({
  postId,
  onOpenChangeAction,
}: SocialPostDialogProps) {
  const t = useTranslations('SocialPostDialog')
  const locale = useLocale()
  const username = 'neuronexis'
  const utils = api.useUtils()

  const [isEditing, setIsEditing] = useState(false)
  const [editCaption, setEditCaption] = useState('')
  const [editHashtags, setEditHashtags] = useState<string[]>([])
  const [hashtagInput, setHashtagInput] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [feedbackText, setFeedbackText] = useState('')
  const [feedbackOpen, setFeedbackOpen] = useState(false)

  const query = api.instagramPosts.get.useQuery(
    { id: postId! },
    { enabled: postId !== null },
  )

  const updateMutation = api.instagramPosts.update.useMutation({
    onSuccess: () => {
      utils.instagramPosts.get.invalidate({ id: postId! })
      utils.instagramPosts.list.invalidate()
      setIsEditing(false)
    },
  })

  const deleteMutation = api.instagramPosts.delete.useMutation({
    onSuccess: () => {
      utils.instagramPosts.list.invalidate()
      onOpenChangeAction(false)
    },
  })

  const feedbackMutation = api.instagramPosts.feedback.useMutation({
    onSuccess: () => {
      toast(t('promptImproved'))
      setFeedbackText('')
      utils.socialPostPrompts.list.invalidate()
    },
  })

  const post = query.data

  const enterEditMode = () => {
    if (!post) return
    setEditCaption(post.caption ?? '')
    setEditHashtags(post.hashtags ?? [])
    setHashtagInput('')
    setIsEditing(true)
  }

  const handleSave = () => {
    if (!post || updateMutation.isPending) return
    updateMutation.mutate({
      id: post.id,
      caption: editCaption,
      hashtags: editHashtags,
    })
  }

  const handleDelete = () => {
    if (!post || deleteMutation.isPending) return
    if (!deleteConfirm) {
      setDeleteConfirm(true)
      return
    }
    deleteMutation.mutate({ id: post.id })
  }

  const handleAddHashtag = () => {
    let tag = hashtagInput.trim()
    if (!tag.startsWith('#')) tag = `#${tag}`
    if (!tag) return
    if (!editHashtags.includes(tag)) setEditHashtags((prev) => [...prev, tag])
    setHashtagInput('')
  }

  const handleFeedbackSubmit = () => {
    if (!post || !feedbackText.trim() || feedbackMutation.isPending) return
    feedbackMutation.mutate({ postId: post.id, feedback: feedbackText })
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setIsEditing(false)
      setDeleteConfirm(false)
      setFeedbackText('')
      setFeedbackOpen(false)
    }
    onOpenChangeAction(open)
  }

  const displayAssets = (post?.assets ?? []).filter(
    (a) => a.url || a.thumbnailUrl,
  )
  const firstAsset = displayAssets[0]
  const mediaType: 'IMAGE' | 'VIDEO' =
    firstAsset?.type === 'VIDEO' ? 'VIDEO' : 'IMAGE'
  const mediaUrl = firstAsset?.url ?? null
  const publishAt = post?.publishAt ? new Date(post.publishAt) : null

  return (
    <Dialog open={postId !== null} onOpenChange={handleOpenChange}>
      <DialogContent
        className="w-[calc(100vw-1rem)] max-h-[calc(100dvh-2rem)] overflow-y-auto p-0 sm:max-w-140 custom-scrollbar"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Post</DialogTitle>

        {query.isLoading && (
          <div className="flex items-center justify-center p-12">
            <span className="inline-block size-6 rounded-full border-2 border-foreground/20 border-t-foreground/70 animate-spin" />
          </div>
        )}

        {post && (
          <div className="flex flex-col">
            {!isEditing && post.platform === 'INSTAGRAM' && (
              <InstagramPostCard
                imageUrl={
                  mediaType === 'VIDEO' ? firstAsset?.thumbnailUrl : mediaUrl
                }
                caption={post.caption}
                hashtags={post.hashtags}
                username={username}
                assets={displayAssets}
              />
            )}
            {!isEditing && post.platform === 'LINKEDIN' && (
              <LinkedInPostCard
                imageUrl={
                  mediaType === 'VIDEO' ? firstAsset?.thumbnailUrl : mediaUrl
                }
                caption={post.caption}
                hashtags={post.hashtags}
                username={username}
                assets={displayAssets}
              />
            )}
            {!isEditing && post.platform === 'TIKTOK' && (
              <TikTokPostCard
                imageUrl={
                  mediaType === 'VIDEO' ? firstAsset?.thumbnailUrl : mediaUrl
                }
                caption={post.caption}
                hashtags={post.hashtags}
                username={username}
                assets={displayAssets}
              />
            )}
            {!isEditing && post.platform === 'FACEBOOK' && (
              <FacebookPostCard
                mediaUrl={mediaUrl}
                mediaType={mediaType}
                caption={post.caption}
                hashtags={post.hashtags}
                username={username}
                assets={displayAssets}
              />
            )}

            {isEditing && (
              <div className="flex flex-col gap-5 p-5">
                {displayAssets.length === 1 ? (
                  <div className="relative overflow-hidden rounded-2xl bg-input/40">
                    {displayAssets[0].type === 'VIDEO' ? (
                      <video
                        src={displayAssets[0].url ?? undefined}
                        controls
                        className="w-full rounded-2xl"
                      />
                    ) : (
                      <div className="aspect-square">
                        <img
                          src={displayAssets[0].url ?? undefined}
                          alt=""
                          className="absolute inset-0 size-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {displayAssets.map((asset, i) => {
                      const displayUrl =
                        asset.type === 'VIDEO' ? asset.thumbnailUrl : asset.url
                      return (
                        <div
                          key={asset.id ?? i}
                          className="relative aspect-square overflow-hidden rounded-xl bg-input/40"
                        >
                          {asset.type === 'VIDEO' ? (
                            <video
                              src={asset.url ?? undefined}
                              controls
                              className="size-full object-cover"
                            />
                          ) : (
                            <img
                              src={displayUrl ?? undefined}
                              alt=""
                              className="size-full object-cover"
                            />
                          )}
                          <span className="absolute bottom-1.5 right-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                            {i + 1}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <Textarea
                    value={editCaption}
                    onChange={(e) => setEditCaption(e.target.value)}
                    rows={5}
                    className="resize-none font-sans text-sm"
                    placeholder="Caption"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap gap-1.5">
                    {editHashtags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() =>
                            setEditHashtags((prev) =>
                              prev.filter((h) => h !== tag),
                            )
                          }
                          className="ml-0.5 text-muted-foreground hover:text-foreground"
                        >
                          <X className="size-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={hashtagInput}
                      onChange={(e) => setHashtagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ',') {
                          e.preventDefault()
                          handleAddHashtag()
                        }
                      }}
                      placeholder="#hashtag"
                      className="flex h-9 flex-1 rounded-xl border border-input bg-transparent px-3 text-sm outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-foreground/20"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddHashtag}
                    >
                      +
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-2 border-t border-border px-5 py-3">
              <div className="flex items-center gap-2">
                {!isEditing && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={enterEditMode}
                    className="gap-1.5"
                  >
                    <Pencil className="size-3.5" />
                    {t('edit')}
                  </Button>
                )}
                {isEditing && (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSave}
                      disabled={updateMutation.isPending}
                    >
                      {updateMutation.isPending ? t('editing') : t('save')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditing(false)}
                      disabled={updateMutation.isPending}
                    >
                      {t('cancel')}
                    </Button>
                  </>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant={deleteConfirm ? 'destructive' : 'ghost'}
                  size="sm"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="gap-1.5"
                  onBlur={() => setDeleteConfirm(false)}
                >
                  <Trash2 className="size-3.5" />
                  {deleteMutation.isPending
                    ? t('deleting')
                    : deleteConfirm
                      ? t('confirmDelete')
                      : t('delete')}
                </Button>
              </div>
            </div>

            {/* Schedule section */}
            <div className="px-5 py-3 border-t border-border flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                {publishAt
                  ? t('scheduledFor', {
                      date: formatPublishAt(publishAt, locale),
                    })
                  : t('notScheduled')}
              </span>
              <PostSchedulePopover postId={post.id} publishAt={publishAt}>
                <Button size="sm" variant="outline">
                  {publishAt ? t('reschedule') : t('schedule')}
                </Button>
              </PostSchedulePopover>
            </div>

            {post.status === 'SUCCESS' && (
              <Collapsible
                open={feedbackOpen}
                onOpenChange={setFeedbackOpen}
                className="border-t border-border"
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-5 py-3 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t('feedbackHeading')}
                    <ChevronDown
                      className={`size-4 transition-transform ${feedbackOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="flex flex-col gap-3 px-5 pb-5">
                    <Textarea
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      placeholder={t('feedbackPlaceholder')}
                      rows={3}
                      className="resize-none text-sm"
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleFeedbackSubmit}
                      disabled={
                        !feedbackText.trim() || feedbackMutation.isPending
                      }
                      className="self-end"
                    >
                      {t('improvePrompt')}
                    </Button>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
